import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed } from '@angular/core';
import { TrafficMatrixComponent } from '@enginuity/analytics/molecules/traffic-matrix/traffic-matrix.component';
import { ChartLoaderWrapperComponent } from '@enginuity/analytics/organisms/analytics-loader/analytics-loading-wrapper.component';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { AnalyticsWidgetComponent } from '@enginuity/analytics/organisms/analytics-widget/analytics-widget.component';
import { CustomBarChartContainerComponent } from '@enginuity/analytics/organisms/custom-bar-chart-container/custom-bar-chart-container.component';
import { HighChartContainerComponent } from '@enginuity/analytics/organisms/high-chart-container/high-chart-container.component';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';
import { ComparePeriod } from '@services/analytics-services/core';
import { Pagination, ProcessingState } from '@services/core-services/models';
import { Subject, takeUntil, switchMap, of } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { DashboardInsightsStore } from './dashboard-insights.store';

@Component({
  selector: 'app-dashboard-insights',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    AnalyticsTrendWidgetComponent,
    TrafficMatrixComponent,
    AnalyticsWidgetComponent,
    CustomBarChartContainerComponent,
    HighChartContainerComponent,
    ChartLoaderWrapperComponent,
    GlobalLoaderWrapperComponent,
  ],
  templateUrl: './dashboard-insights.component.html',
  styleUrl: './dashboard-insights.component.scss',
  providers: [DashboardInsightsStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardInsightsComponent implements OnInit, OnDestroy {
  protected comparePeriod!: ComparePeriod;
  private destroy$ = new Subject<void>();

  visitorMetricTitles: string[] = ['Visitors'];
  protected readonly ProcessingState = ProcessingState;

  protected defaultPagination: Pagination = {
    limit: 10,
    total_records: 0,
    total_pages: 1,
    next_page: null,
    previous_page: null,
  };

  // Get signals directly from the store
  protected rangeContext = this.dashboardStore.getRangeContextSignal();

  // Create resources using the new resource pattern
  protected activeUsersResource = this.dashboardInsightsStore.createActiveUsersResource(
    this.rangeContext
  );

  protected visitorAnalyticsResource = this.dashboardInsightsStore.createVisitorAnalyticsResource(
    this.rangeContext
  );

  protected viewAnalyticsResource = this.dashboardInsightsStore.createViewAnalyticsResource(
    this.rangeContext
  );

  protected eventAnalyticsResource = this.dashboardInsightsStore.createEventAnalyticsResource(
    this.rangeContext
  );

  protected trafficMatrixResource = this.dashboardInsightsStore.createTrafficMatrixResource(
    this.rangeContext
  );

  protected pageStatsChartResource = this.dashboardInsightsStore.createPageStatsChartResource(
    this.rangeContext
  );

  protected visitorsByDeviceChartResource =
    this.dashboardInsightsStore.createVisitorsByDeviceChartResource(this.rangeContext);

  protected serverEventsChartResource = this.dashboardInsightsStore.createServerEventsChartResource(
    this.rangeContext
  );

  // Visitors by source - keeping existing Observable pattern (not migrated yet)
  protected visitorsBySourceStats$ = this.dashboardInsightsStore.getVisitorsBySourceStats();
  protected visitorsBySourceTabs$ = this.dashboardInsightsStore.getVisitorsBySourceTabs();
  protected visitorsBySourceActiveTab$ = this.dashboardInsightsStore.getVisitorsBySourceActiveTab();
  protected visitorsBySourcePagination$ =
    this.dashboardInsightsStore.getVisitorsBySourcePagination();
  protected visitorsBySourceLoader$ = this.dashboardInsightsStore.getVisitorsBySourceLoader();

  // Computed signals for easier template access
  protected context = computed(() => this.rangeContext());

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardInsightsStore: DashboardInsightsStore
  ) {}

  ngOnInit() {
    // Only subscribe to compare period since resources handle data loading automatically
    this.dashboardStore
      .getComparePeriod()
      .pipe(takeUntil(this.destroy$))
      .subscribe(period => (this.comparePeriod = period));
    this.dashboardStore
      .getRangeContext()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(context =>
          context ? this.dashboardInsightsStore.loadVisitorsBySourceData(context) : of(null)
        )
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onVisitorsTabChange(tabId: string): void {
    this.dashboardInsightsStore.updateVisitorsBySourceDefaultTab(tabId);
  }

  protected onVisitorsPageChange(event: { tabId: string; page: number; limit: number }): void {
    this.dashboardInsightsStore.updateVisitorsBySourcePagination(
      event.tabId,
      event.page,
      event.limit
    );
  }
}
